/**
 * This is a wrapper component for different Layouts.
 * Navigational 'aside' content should be added to this wrapper.
 */
import React from 'react';
import { useHistory } from 'react-router-dom';

import { useRouteConfiguration } from '../../../context/routeConfigurationContext';
import { FormattedMessage } from '../../../util/reactIntl';
import { pathByRouteName } from '../../../util/routes';

import { TabNav } from '../../../components';

import css from './LayoutSideNavigation.module.css';

/**
 * Side nav with navigation to different account settings.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.accountSettingsNavProps
 * @param {string?} props.accountSettingsNavProps.currentPage
 * @param {boolean?} props.accountSettingsNavProps.showPaymentMethods
 * @param {boolean?} props.accountSettingsNavProps.showPayoutDetails
 * @returns {JSX.Element} Side nav with navigation to different account settings
 */
const LayoutWrapperAccountSettingsSideNav = props => {
  const { accountSettingsNavProps, ariaLabel } = props;
  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();

  const { currentPage, showPaymentMethods, showPayoutDetails } = accountSettingsNavProps;
  const payoutDetailsMaybe = showPayoutDetails
    ? [
        {
          routeName: 'StripePayoutPage',
          text: <FormattedMessage id="LayoutWrapperAccountSettingsSideNav.paymentsTabTitle" />,
          selected: currentPage === 'StripePayoutPage',
          id: 'StripePayoutPageTab',
          linkProps: {
            name: 'StripePayoutPage',
          },
        },
      ]
    : [];

  const paymentMethodsMaybe = showPaymentMethods
    ? [
        {
          routeName: 'PaymentMethodsPage',
          text: (
            <FormattedMessage id="LayoutWrapperAccountSettingsSideNav.paymentMethodsTabTitle" />
          ),
          selected: currentPage === 'PaymentMethodsPage',
          id: 'PaymentMethodsPageTab',
          linkProps: {
            name: 'PaymentMethodsPage',
          },
        },
      ]
    : [];

  const tabs = [
    {
      routeName: 'ContactDetailsPage',
      text: <FormattedMessage id="LayoutWrapperAccountSettingsSideNav.contactDetailsTabTitle" />,
      selected: currentPage === 'ContactDetailsPage',
      id: 'ContactDetailsPageTab',
      linkProps: {
        name: 'ContactDetailsPage',
      },
    },
    {
      routeName: 'PasswordChangePage',
      text: <FormattedMessage id="LayoutWrapperAccountSettingsSideNav.passwordTabTitle" />,
      selected: currentPage === 'PasswordChangePage',
      id: 'PasswordChangePageTab',
      linkProps: {
        name: 'PasswordChangePage',
      },
    },
    ...payoutDetailsMaybe,
    ...paymentMethodsMaybe,
    {
      routeName: 'ManageAccountPage',
      text: <FormattedMessage id="LayoutWrapperAccountSettingsSideNav.manageAccountTabTitle" />,
      selected: currentPage === 'ManageAccountPage',
      id: 'ManageAccountPageTab',
      linkProps: {
        name: 'ManageAccountPage',
      },
    },
  ];

  const tabsForNavigation = tabs.map(({ routeName, ...tab }) => tab);

  return (
    <>
      <div className={css.mobileSelector}>
        <label className={css.mobileSelectorLabel} htmlFor="accountSettingsSection">
          <FormattedMessage id="LayoutWrapperAccountSettingsSideNav.manageAccountTabTitle" />
        </label>
        <select
          id="accountSettingsSection"
          aria-label={ariaLabel}
          value={currentPage}
          onChange={event =>
            history.push(pathByRouteName(event.target.value, routeConfiguration))
          }
        >
          {tabs.map(tab => (
            <option key={tab.routeName} value={tab.routeName}>
              {tab.text}
            </option>
          ))}
        </select>
      </div>
      <TabNav
        rootClassName={css.tabs}
        tabRootClassName={css.tab}
        tabs={tabsForNavigation}
        ariaLabel={ariaLabel}
      />
    </>
  );
};

export default LayoutWrapperAccountSettingsSideNav;
